<template>
    <portal to="dash">
        <div class="shorten-link-modal" @click.self="close">
            <div class="popup">
                <div class="title-section">
                    <h2>{{ $t('dash.links.modal.title') }}</h2>
                    <p>{{ $t('dash.links.modal.subtitle') }}</p>
                </div>

                <template v-if="!created">
                    <div class="container-input">
                        <label>{{ $t('dash.links.form.url.label') }}</label>
                        <div class="input">
                            <i class="fa-solid fa-link"></i>
                            <input
                                ref="urlInput"
                                type="url"
                                :placeholder="$t('dash.links.form.url.placeholder')"
                                v-model="form.url"
                                @keypress.enter.prevent="submit"
                            >
                        </div>
                    </div>

                    <div class="container-input">
                        <label>{{ $t('dash.links.form.label.label') }}</label>
                        <div class="input">
                            <i class="fa-solid fa-tag"></i>
                            <input
                                :placeholder="$t('dash.links.form.label.placeholder')"
                                v-model="form.label"
                                maxlength="128"
                                @keypress.enter.prevent="submit"
                            >
                        </div>
                    </div>

                    <p class="error" v-if="error"><i class="fa-solid fa-circle-exclamation"></i> {{ error }}</p>

                    <div class="btns">
                        <div class="btn-outline-primary" @click="close">{{ $t('dash.button.cancel') }}</div>
                        <div class="btn-primary" :class="{ disabled: loading || !form.url.trim() }" @click="submit">
                            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
                            {{ $t('dash.links.form.submit') }}
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="result">
                        <a class="short-url" :href="created.short_url" target="_blank" rel="noopener">{{ created.short_url }}</a>
                        <p class="destination" :title="created.url">{{ created.url }}</p>
                    </div>

                    <div class="btns">
                        <div class="btn-outline-primary" @click="reset">{{ $t('dash.links.modal.another') }}</div>
                        <div class="btn-primary" @click="copy">
                            <i class="fa-regular" :class="copied ? 'fa-check' : 'fa-copy'"></i>
                            {{ insertLabel || $t('dash.links.copy') }}
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </portal>
</template>

<script>
import ShortLinksMixin from '@/components/mixins/ShortLinks.vue'

// Création d'un lien court à la volée (page Liens courts, post creator).
// Émet 'created' (lien créé), 'use' (bouton principal du résultat, avec le lien) et 'close'.
export default {
    name: 'ShortenLinkModal',

    mixins: [ShortLinksMixin],

    props: {
        defaultUrl: { type: String, default: '' },
        // Libellé du bouton principal une fois le lien créé (ex. « Insérer dans le texte ») ; copie par défaut
        insertLabel: { type: String, default: '' }
    },

    data() {
        return {
            form: { url: this.defaultUrl, label: '' },
            loading: false,
            error: null,
            created: null,
            copied: false
        }
    },

    mounted() {
        this.$nextTick(() => this.$refs.urlInput && this.$refs.urlInput.focus())
    },

    methods: {
        isValidUrl(url) {
            try {
                const parsed = new URL(url)
                return ['http:', 'https:'].includes(parsed.protocol)
            } catch (e) {
                return false
            }
        },

        async submit() {
            if (this.loading) return
            const url = this.form.url.trim()
            if (!url) return
            if (!this.isValidUrl(url)) {
                this.error = this.$t('dash.error.short_link_invalid_url')
                return
            }

            this.loading = true
            this.error = null
            const body = { url }
            if (this.form.label.trim()) body.label = this.form.label.trim()

            const res = await this.createShortLink(body)
            this.loading = false

            if (!res || !res.ok || !res.body || !res.body.short_url) {
                this.error = this.shortLinkErrorMessage(res)
                return
            }

            this.created = res.body
            this.$emit('created', res.body)
        },

        async copy() {
            if (!this.created) return
            if (this.insertLabel) {
                this.$emit('use', this.created)
                return
            }
            try {
                await navigator.clipboard.writeText(this.created.short_url)
                this.copied = true
                setTimeout(() => { this.copied = false }, 2000)
            } catch (e) {
                this.$vs.notify({ title: this.$t('dash.notifications.error_title'), text: this.$t('dash.links.copy_failed'), color: 'danger' })
            }
        },

        reset() {
            this.form = { url: '', label: '' }
            this.created = null
            this.error = null
            this.$nextTick(() => this.$refs.urlInput && this.$refs.urlInput.focus())
        },

        close() {
            this.$emit('close')
        }
    }
}
</script>

<style scoped>
.shorten-link-modal {
    position: fixed;
    inset: 0;
    z-index: 30000;
    background: rgba(47, 25, 55, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.popup {
    background: #fff;
    border-radius: 12px;
    width: 100%;
    max-width: 480px;
    padding: 24px;
    box-shadow: 0 8px 32px rgba(107, 31, 77, 0.18);
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.title-section h2 {
    font-size: 18px;
    margin: 0 0 4px;
    color: #2F1937;
}
.title-section p {
    margin: 0;
    font-size: 13px;
    color: #555E75;
}
.container-input label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #2F1937;
}
.container-input .input {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1.5px solid #e0d0ea;
    border-radius: 8px;
    padding: 8px 12px;
}
.container-input .input i {
    color: #6B1F4D;
}
.container-input .input input {
    border: none;
    outline: none;
    flex: 1;
    min-width: 0;
    font-size: 14px;
}
.error {
    margin: 0;
    color: #d0263b;
    font-size: 13px;
}
.result {
    border: 1.5px solid #f1edf5;
    background: #f9f6fb;
    border-radius: 8px;
    padding: 12px;
}
.short-url {
    font-size: 18px;
    font-weight: 600;
    color: #B20564;
    word-break: break-all;
}
.destination {
    margin: 6px 0 0;
    font-size: 12px;
    color: #555E75;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.btns {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.btns .disabled {
    opacity: 0.5;
    pointer-events: none;
}
</style>
